import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import LoginModal from './TeamPublicPage/LoginModal'
import GoogleLoginButton from 'components/shared/GoogleLoginButton'
import { isInAppBrowser } from 'utils'
import { save, load, clear } from 'utils/studentRequestDraft'
import { orderTagsByGoals, flowForPrefill, stepIdsFor, resumeStepFor } from 'utils/studentRequestSteps'
import { createFindTeacherTracker, sendLeaveOnExit } from 'utils/findTeacherTracking'
import { csrfToken } from 'utils/csrfToken'
import AcceptedTeachersSummary from './AcceptedTeachersSummary'

// Mirrors the model validation, which in turn sits under the limit on the
// message posted into the conversation.
const SITUATION_MAX_LENGTH = 1500
// The wizard's screens as the funnel events name them.
const TRACKER_SCREENS = {
  questionnaire: 'intro',
  active: 'active_request',
  register: 'registration',
  matched: 'confirmation',
  awaiting_confirmation: 'confirmation'
}

function trackerScreenFor(screen) {
  return TRACKER_SCREENS[screen] || 'intro'
}

// Specialties shown before the visitor asks for the rest.
const VISIBLE_TAGS = 6

// Days are submitted as picked. Only the period labels expand, using the table
// the server sends, so the questionnaire and the matcher cannot disagree about
// what an evening is.
function expandSchedule(days, periods, dayPeriodTable) {
  const slots = [...new Set(periods.flatMap((period) => dayPeriodTable[period] || []))]
  return { desired_days: [...days].sort((a, b) => a - b), desired_time_slots: slots }
}

// One shape for every pickable row. Warm neutral at rest so the brand blue
// stays rare enough to read as "you chose this" rather than as decoration.
const OPTION_BASE =
  'flex cursor-pointer items-center rounded-xl border px-4 py-3.5 transition-colors duration-150'
const OPTION_REST = 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50'
const OPTION_PICKED = 'border-[#3366FF] bg-[#E6F0FF]'

function optionClass(picked, extra = '') {
  return `${OPTION_BASE} ${picked ? OPTION_PICKED : OPTION_REST} ${extra}`.trim()
}

// min-h-11 is the 44px touch target a thumb needs; these were 34px tall.
const SHORTCUT_BUTTON =
  'inline-flex min-h-11 items-center rounded-lg border border-stone-200 bg-white px-4 text-sm text-stone-600 transition-colors hover:border-stone-300 hover:bg-stone-50'

const PRIMARY_BUTTON =
  'rounded-xl bg-[#3366FF] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#2952CC] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3366FF] disabled:opacity-60'


export default function StudentRequestWizard({ config }) {
  const t = config.translations || {}
  const steps = t.steps || {}
  const errors = t.errors || {}
  // What the page that linked here already knows. Absent for a visitor who
  // arrived any other way, and the server withholds it on every screen that is
  // not the questionnaire.
  const prefill = config.prefill || null
  const [step, setStep] = useState(0)
  const [subject, setSubject] = useState(prefill?.subject ?? null)
  // ⛔ Preselected, never skipped. The level is converted from an IELTS band by
  // an estimate, so the visitor has to be able to see it to correct it.
  const [level, setLevel] = useState(prefill?.level ?? null)
  const [goals, setGoals] = useState(prefill?.goals ?? [])
  const [tags, setTags] = useState(prefill?.tags ?? [])
  const [days, setDays] = useState([])
  const [periods, setPeriods] = useState([])
  const [situation, setSituation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  // "questionnaire" | "register" | "awaiting_confirmation" | "matched".
  // The server decides the opening state because the registration modal reloads
  // the page, so nothing the wizard holds in memory survives that.
  const [screen, setScreen] = useState(config.initial_state || 'questionnaire')
  const [showLogin, setShowLogin] = useState(false)
  // The request in progress can be sent to another round of teachers. Held in
  // state because the answer changes the screen without a reload: the url is
  // dropped once the ceiling is reached, and the outcome line has to say which
  // of the three things happened instead of leaving the button looking dead.
  const [rematchUrl, setRematchUrl] = useState(config.active_request?.rematch_url || null)
  const [rematchNote, setRematchNote] = useState(null)
  // Set by the cancel action's reload. Read once: a refresh afterwards should
  // not repeat the notice.
  const [cancelled] = useState(() => new URLSearchParams(window.location.search).has('cancelled'))
  // Only true on a reload the server answered with "register": the visitor
  // submitted, never registered, and is coming back to a page that would
  // otherwise give them no way back to their answers.
  const [submittedPrompt, setSubmittedPrompt] = useState(config.initial_state === 'register')
  // A draft found in this browser, held aside until the visitor says whether
  // they want it. Applying it unasked would overwrite a fresh start.
  const [resumable, setResumable] = useState(null)
  // Set when the visitor asks for the skipped questions back, or when a draft
  // is resumed: their own half-finished answers outrank what a link inferred.
  const [prefillDropped, setPrefillDropped] = useState(false)
  // One tracker per page load: every event shares its flow_id, and it sends the
  // single leave event that says where the visitor stopped.
  const trackerRef = useRef(null)
  if (!trackerRef.current) trackerRef.current = createFindTeacherTracker()
  const tracker = trackerRef.current
  const startedRef = useRef(false)

  useEffect(() => {
    const initial = config.initial_state || 'questionnaire'
    const hasDraft = initial === 'questionnaire' && Boolean(load())
    const initialScreen = initial === 'questionnaire' && hasDraft ? 'resume' : trackerScreenFor(initial)
    tracker.setScreen(initialScreen)
    tracker.track('find_teacher_view', {
      prefilled: Boolean(prefill),
      has_draft: hasDraft,
      signed_in: Boolean(config.signed_in),
      initial_screen: initialScreen
    })
    // The request exists once an account claims it, which happens on the
    // registration round trip, not on submit. Sent once per request: the
    // flag survives the reloads that follow.
    const claimed = config.active_request
    if (claimed?.claimed_recently) {
      const key = `tn.request_created.${claimed.public_uuid}`
      let seen = false
      try { seen = Boolean(localStorage.getItem(key)) } catch { seen = false }
      if (!seen) {
        tracker.track('request_created', { subject: claimed.subject_key || '' })
        try { localStorage.setItem(key, '1') } catch { /* storage unavailable */ }
      }
    }
    return sendLeaveOnExit(tracker)
    // Runs once per page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (screen !== 'questionnaire') return

    const stored = load()
    if (stored) {
      setResumable(stored)
      tracker.track('draft_resume_shown')
    }
    // Runs once: a draft is offered when the page opens, not again mid-answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Ordered options for the chosen subject, and the step list that follows from
  // whether any are left.
  const tagOptions = orderTagsByGoals(
    (config.subject_tag_options || {})[subject] || [],
    goals,
    config.goal_tag_order || {}
  )
  // Level and goals are language questions. A maths student's stage and purpose
  // are both already in the specialty list, so asking them is noise.
  const asksLevelAndGoals = !subject || (config.language_subjects || []).includes(subject)
  // ⛔ A draft outranks the link the moment it is found, not when the visitor
  // accepts it: while the resume prompt is up, every question is back in the
  // flow. Discarding the draft is a fresh start, and the link's answers are
  // then the right place to begin again.
  const flow = flowForPrefill(prefillDropped || resumable ? null : prefill, tagOptions.length, asksLevelAndGoals)
  const stepIds = flow.stepIds
  // The level question names the subject. Calling it "this language" was wrong
  // for maths, and vague for everything else once the subject is already known.
  const subjectLabel = (config.subjects || []).find((option) => option.value === subject)?.label || ''
  // "Near-native" describes fluency in a language. On maths it is a level
  // nobody can place themselves on.
  const levelOptions = (config.levels || []).filter((option) => (
    option.value !== 'near_native' || (config.language_subjects || []).includes(subject)
  ))
  const stepCount = stepIds.length
  // Clamped, because the list shrinks when the visitor picks a subject with no
  // tags left to ask about. Without this the progress bar can read past 100%.
  const stepIndex = Math.min(step, stepCount - 1)
  const currentStep = stepIds[stepIndex]
  const maxTags = config.max_subject_tags || 3

  // A long specialty list buries "next" on a phone. The first few are already
  // the ones the chosen goals point to; the rest open on request. A picked tag
  // always stays visible, so folding never hides an answer.
  const [tagsExpanded, setTagsExpanded] = useState(false)
  useEffect(() => { setTagsExpanded(false) }, [currentStep])
  const visibleTagOptions = tagsExpanded
    ? tagOptions
    : tagOptions.filter((option, index) => index < VISIBLE_TAGS || tags.includes(option.value))
  const hiddenTagCount = tagOptions.length - visibleTagOptions.length

  const selectionCount = (id) => ({
    subject: subject ? 1 : 0,
    level_goals: (level ? 1 : 0) + goals.length,
    tags: tags.length,
    schedule_note: days.length + periods.length
  }[id] || 0)

  // Every deliberate interaction. The first one is what starts the
  // questionnaire: the first question sits on the opening screen, so there is
  // no separate start button to count.
  const record = (name) => {
    tracker.action(name)
    if (!startedRef.current) {
      startedRef.current = true
      tracker.track('wizard_start', { first_step: currentStep })
    }
  }

  // Before the first interaction the visitor is still reading the opening
  // screen; after it, they are on a step.
  const questionnaireStarted = startedRef.current
  useEffect(() => {
    if (screen !== 'questionnaire' || resumable) return
    tracker.track('wizard_step_view', { step: currentStep, step_index: stepIndex, subject: subject || '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, currentStep])

  useEffect(() => {
    if (screen === 'questionnaire') {
      tracker.setScreen(
        questionnaireStarted ? 'step' : 'intro',
        questionnaireStarted ? currentStep : '',
        { keepTimer: questionnaireStarted && stepIndex === 0 }
      )
    } else {
      tracker.setScreen(trackerScreenFor(screen))
      if (screen === 'register') tracker.track('registration_view')
      if (screen === 'matched' || screen === 'awaiting_confirmation') {
        tracker.track('confirmation_view', { path: screen === 'matched' ? 'google' : 'email' })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, currentStep, questionnaireStarted])

  // Tags belong to one subject. Keeping them across a subject change would
  // submit a tag the new subject does not offer, which the API rejects with a
  // message the visitor cannot act on: the specialty step never showed that tag
  // as chosen, because it is not in the new subject's list.
  const chooseSubject = (value) => {
    record('select_subject')
    if (value !== subject) setTags([])
    // A subject that skips these questions must not carry answers from one that
    // asked them: mapped_tags turns the level into tags, so a maths request
    // holding "intermediate" would be scored against language tags.
    if (!(config.language_subjects || []).includes(value)) {
      setLevel(null)
      setGoals([])
    }
    setSubject(value)
  }

  // Written on every step change rather than on every keystroke: the step
  // boundary is where someone actually leaves.
  const goToStep = (next) => {
    setStep(next)
    // ⛔ The draft stores a position in the full question list, not in this
    // visit's shortened one. A prefilled visit that saved index 1 would
    // otherwise come back, without the link, pointing at a different question.
    save({ subject, level, goals, tags, days, periods, situation }, flow.allStepIds.indexOf(stepIds[next]))
  }

  // On the first shown step, "back" has somewhere to go after all: the
  // questions the link answered. ⛔ Without it the visitor who arrived from an
  // IELTS page could never change the subject to TOEIC.
  // ⛔ A skipped fact always arrives with its subject, so a non-empty `skipped`
  // always holds the subject question and the first shown step is never it.
  // The server refuses a fact that arrives without one.
  const atSkippedBoundary = stepIndex === 0 && flow.skipped.length > 0

  // The skipped questions come back with the answers kept.
  const restoreSkippedSteps = () => {
    setPrefillDropped(true)
    setStep(0)
  }

  const applyResumable = () => {
    tracker.action('resume_continue')
    tracker.track('draft_resume_choice', { choice: 'continue' })
    // Their own unfinished answers outrank anything the link inferred.
    setPrefillDropped(true)
    const a = resumable.answers || {}
    setSubject(a.subject ?? null)
    setLevel(a.level ?? null)
    setGoals(a.goals ?? [])
    setTags(a.tags ?? [])
    setDays(a.days ?? [])
    setPeriods(a.periods ?? [])
    setSituation(a.situation ?? '')
    // ⛔ Read from the answers, not the stored index: a draft saved under the
    // six-step layout holds a number that now points at a different screen.
    const resumedSubject = a.subject ?? null
    const resumedSteps = stepIdsFor(
      ((config.subject_tag_options || {})[resumedSubject] || []).length,
      !resumedSubject || (config.language_subjects || []).includes(resumedSubject)
    )
    setStep(resumeStepFor(a, resumedSteps))
    setResumable(null)
  }

  const discardResumable = () => {
    tracker.action('resume_restart')
    tracker.track('draft_resume_choice', { choice: 'restart' })
    clear()
    setResumable(null)
  }

  const toggle = (list, setList, value, actionName = 'toggle_option') => {
    record(actionName)
    setErrorMessage(null)
    setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value])
  }

  // A shortcut adds its days, or clears them when they are all already picked,
  // so tapping "weekdays" twice undoes it instead of doing nothing.
  const applyShortcut = (current, groupDays) => {
    const allPicked = groupDays.every((day) => current.includes(day))
    return allPicked
      ? current.filter((day) => !groupDays.includes(day))
      : [...new Set([...current, ...groupDays])]
  }

  const canAdvance = () => {
    if (currentStep === 'subject') return Boolean(subject)
    // Level, goals and specialty tags are skippable on purpose: a student who
    // cannot place themselves is exactly who this page is for.
    if (currentStep === 'schedule_note') return days.length > 0 && periods.length > 0
    return true
  }

  // What a failed submission was, so a refusal is never read as abandonment.
  const submitError = (status, message) => {
    if (status === 429) return 'rate_limited'
    if (status >= 500) return 'server'
    const known = { stale_session: errors.stale_session, subject_already_open: errors.subject_already_open }
    const hit = Object.entries(known).find(([, text]) => text && text === message)
    if (hit) return hit[0]
    if (message && errors.quota_reached && message.startsWith(errors.quota_reached.split('%{')[0])) return 'quota_reached'
    return 'validation'
  }

  const submit = async () => {
    if (submitting) return
    record('submit')
    // The schedule shares the last screen with the note, so no "next" press
    // checks it before submitting any more.
    if (!canAdvance()) {
      tracker.track('wizard_validation_error', { step: currentStep, error: 'schedule_required' })
      setErrorMessage(errors.schedule_required)
      return
    }
    tracker.track('submit_attempt', { subject: subject || '', answered_steps: stepIndex })
    setSubmitting(true)
    setErrorMessage(null)

    const schedule = expandSchedule(days, periods, config.day_periods)

    try {
      const response = await fetch(config.submit_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({
          student_request: {
            subject,
            level,
            goals,
            subject_tags: tags,
            situation,
            ...schedule
          }
        })
      })

      if (response.status === 429) {
        // Answers stay on screen: retyping four steps is the fastest way to
        // lose somebody who already decided to ask for help.
        tracker.track('submit_result', { ok: false, error: 'rate_limited' })
        setErrorMessage(errors.rate_limited)
        return
      }

      if (!response.ok) {
        const failure = await response.json().catch(() => ({}))
        tracker.track('submit_result', { ok: false, error: submitError(response.status, failure.errors?.[0]) })
        setErrorMessage(failure.errors?.[0] || errors.generic)
        return
      }

      const body = await response.json().catch(() => ({}))

      tracker.track('submit_result', { ok: true, error: '' })
      // A signed-in visitor's submission is claimed on the spot. An anonymous
      // one is a draft until they register.
      tracker.track(body.data?.status === 'matching' ? 'request_created' : 'request_drafted', { subject: subject || '' })
      tracker.completeStep()
      tracker.markCompleted()

      // The request exists now. Leaving the draft behind would offer to resume
      // a questionnaire the visitor already sent.
      clear()
      setScreen(body.data?.status === 'matching' ? 'matched' : 'register')
    } catch {
      tracker.track('submit_result', { ok: false, error: 'network' })
      setErrorMessage(errors.generic)
    } finally {
      setSubmitting(false)
    }
  }

  const renderResumeModal = ({ title, body, continueLabel, onContinue, onRestart }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 lg:p-4">
      {/* flex-col with a flex-1 body is what the modal standard asks for. Without
          it the phone shows two lines of text, the buttons directly under them,
          and then half a screen of nothing, with the buttons out of thumb reach. */}
      <div className="flex h-full w-full flex-col overflow-hidden bg-white lg:mx-4 lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:rounded-xl lg:shadow-xl">
        <div className="border-b px-6 py-4" style={{ backgroundColor: '#E6F0FF', color: '#3366FF' }}>
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <p className="leading-relaxed text-stone-600">{body}</p>
        </div>
        <div className="flex justify-end gap-3 border-t border-stone-100 p-6">
          <button
            type="button"
            onClick={onRestart}
            className="min-h-11 rounded-lg border border-stone-300 px-5 text-stone-700 transition-colors hover:bg-stone-50"
          >
            {t.resume?.restart}
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="min-h-11 rounded-lg bg-[#3366FF] px-5 font-semibold text-white transition-colors hover:bg-[#2952CC]"
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  )

  // Starting over after a submission does not delete the row. The API keys the
  // draft to this browser's token, so the next submission updates the same
  // record rather than leaving an orphan behind.
  const restartAfterSubmission = () => {
    setSubmittedPrompt(false)
    clear()
    setSubject(null)
    setLevel(null)
    setGoals([])
    setTags([])
    setDays([])
    setPeriods([])
    setSituation('')
    setStep(0)
    setScreen('questionnaire')
  }

  const confirmation = t.confirmation || {}
  const active = config.active_request

  if (screen === 'active' && active) {
    return (
      <div className="mx-auto max-w-lg px-6 py-16">
        <h1 className="text-[1.75rem] font-semibold tracking-tight text-stone-900">
          {t.active?.title}
        </h1>
        <div className="mt-6 rounded-2xl border border-stone-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(68,60,50,0.05),0_14px_30px_-18px_rgba(68,60,50,0.22)]">
          <p className="font-medium text-stone-900">{active.subject}</p>
          <p className="mt-1 text-sm text-stone-500">{active.schedule}</p>
          {/* Someone who registered by email never sees the confirmation screen:
              they confirm, sign in, and land straight here. Without this they are
              never told that more than one teacher may write to them. */}
          {active.accepted_count === 0 ? (
            <p className="mt-2 text-sm text-stone-500">{t.active?.waiting_note}</p>
          ) : (
            // The platform's promise is a choice of teachers, so the number is
            // the thing the student came back to find out.
            <p className="mt-2 text-sm font-medium text-stone-900">
              {((active.accepted_count === 1 && t.active?.accepted_count_one) || t.active?.accepted_count || '').replace('%{count}', String(active.accepted_count))}
            </p>
          )}
          <AcceptedTeachersSummary
            teachers={active.accepted_teachers}
            moreCount={active.accepted_more_count || 0}
            t={t.active || {}}
          />
          {active.request_page_url && (
            <a
              href={active.request_page_url}
              className="mt-4 inline-block rounded-xl bg-[#3366FF] px-5 py-2.5 font-medium text-white transition-colors hover:bg-[#2952CC] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3366FF]"
            >
              {t.active?.open_request}
            </a>
          )}
        </div>

        {errorMessage && (
          <p className="mt-4 text-sm text-red-700" role="alert">
            {errorMessage}
          </p>
        )}

        {rematchUrl && (
          <button
            type="button"
            disabled={submitting}
            onClick={async () => {
              tracker.action('rematch')
              tracker.track('rematch_click', { accepted_count: active.accepted_count })
              setSubmitting(true)
              setRematchNote(null)
              setErrorMessage(null)
              try {
                const response = await fetch(rematchUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() }
                })
                if (!response.ok) {
                  setErrorMessage(errors.generic)
                  return
                }
                const body = await response.json()
                setRematchNote(
                  body.added > 0
                    ? (t.active?.rematch_added || '').replace('%{count}', body.added)
                    : t.active?.rematch_none
                )
                // Hidden rather than left to answer "nobody new" forever once
                // the request has had every round it is allowed.
                if (!body.can_retry) setRematchUrl(null)
              } catch {
                setErrorMessage(errors.generic)
              } finally {
                setSubmitting(false)
              }
            }}
            className="mt-6 w-full rounded-xl border border-stone-300 bg-white px-5 py-3 font-medium text-stone-800 transition-colors hover:border-stone-400 hover:bg-stone-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3366FF] disabled:opacity-60"
          >
            {t.active?.rematch}
          </button>
        )}

        {rematchNote && (
          <p className="mt-3 text-sm text-stone-600" role="status">
            {rematchNote}
          </p>
        )}

        <button
          type="button"
          disabled={submitting}
          onClick={async () => {
            // Closing the request frees the quota and cannot be undone from
            // here, so it asks first.
            if (!window.confirm(t.active?.cancel_confirm)) return
            tracker.action('cancel')
            tracker.track('request_cancel', { accepted_count: active.accepted_count })

            setSubmitting(true)
            try {
              const response = await fetch(active.cancel_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() }
              })
              if (response.ok) {
                // Carried in the URL because the page reloads into a blank
                // questionnaire, where nothing else would say what happened.
                window.location.href = `${window.location.pathname}?cancelled=1`
                return
              }
              setErrorMessage(errors.generic)
            } catch {
              setErrorMessage(errors.generic)
            } finally {
              setSubmitting(false)
            }
          }}
          // Stays a quiet text link, but 20px of it was not a target a thumb
          // can hit. min-h-11 grows the tap area without growing the link.
          className="mt-4 inline-flex min-h-11 items-center text-sm text-stone-500 underline underline-offset-4 transition-colors hover:text-stone-800 disabled:opacity-60"
        >
          {t.active?.cancel}
        </button>
      </div>
    )
  }

  if (screen === 'matched') {
    // Matching runs after the claim, so no teacher count exists yet. The steps
    // say what the platform is doing with the answers instead.
    const matchSteps = confirmation.match_steps || {}
    const matchLines = [
      config.active_request?.asks_level === false ? null : matchSteps.level_goals,
      matchSteps.tags,
      matchSteps.schedule
    ].filter(Boolean)

    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        <h1 className="text-[1.75rem] font-semibold tracking-tight text-stone-900">{confirmation.google_title}</h1>
        <ul className="mx-auto mt-6 inline-flex flex-col gap-2 text-left text-stone-700">
          {matchLines.map((line) => (
            <li key={line} className="flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#3366FF]" aria-hidden="true" />
              {line}
            </li>
          ))}
        </ul>
        <p className="mt-6 leading-relaxed text-stone-600">{confirmation.google_body}</p>
        <p className="mt-2 leading-relaxed text-stone-600">{confirmation.google_inbox}</p>
      </div>
    )
  }

  // Registered by email, link not yet clicked. Says plainly that nothing starts
  // until they click — a student told only "check your inbox" waits for a
  // teacher who was never notified.
  if (screen === 'awaiting_confirmation') {
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        <h1 className="text-[1.75rem] font-semibold tracking-tight text-stone-900">{confirmation.email_title}</h1>
        <p className="mt-4 leading-relaxed text-stone-600">{confirmation.email_body}</p>
        <p className="mt-2 font-medium text-stone-900">{confirmation.email_blocking}</p>
        <p className="mt-2 text-sm text-stone-500">{confirmation.email_spam}</p>
      </div>
    )
  }

  if (screen === 'register') {
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        {submittedPrompt && renderResumeModal({
          title: t.resume?.submitted_title,
          body: t.resume?.submitted_body,
          continueLabel: t.resume?.submitted_continue,
          onContinue: () => setSubmittedPrompt(false),
          onRestart: restartAfterSubmission
        })}

        <h1 className="text-[1.75rem] font-semibold tracking-tight text-stone-900">{t.registration?.title}</h1>
        <p className="mt-4 leading-relaxed text-stone-600">{t.registration?.assurance_no_sales}</p>
        <p className="leading-relaxed text-stone-600">{t.registration?.assurance_teacher_contacts}</p>

        {/* Google is the path almost everyone takes, so it is the button rather
            than what the button opens. Hiding it behind a modal only delays
            telling the visitor how little this step costs, at the moment they
            are least sure they want to pay it.

            An in-app browser cannot finish Google OAuth, so those visitors keep
            the single button, and the modal opens straight onto its email
            flow. */}
        {isInAppBrowser() ? (
          <button
            type="button"
            onClick={() => {
              tracker.action('registration_email')
              tracker.track('registration_method', { method: 'email' })
              setShowLogin(true)
            }}
            className="mt-8 inline-block rounded-xl bg-[#3366FF] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#2952CC] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3366FF]"
          >
            {t.registration?.cta}
          </button>
        ) : (
          <div
            className="mt-8"
            onClickCapture={(event) => {
              // The Google button is a link out of the page; the email link
              // below reports itself.
              if (event.target.closest('button')?.dataset.registration === 'email') return
              tracker.action('registration_google')
              tracker.track('registration_method', { method: 'google' })
            }}
          >
            <GoogleLoginButton
              role="student"
              label={config.login_translations?.loginModal?.googleButton}
              extraParams={{ redirect_url: config.after_sign_in_url }}
              translations={config.login_translations?.inAppBrowser}
              className="py-3.5 border-2 rounded-xl justify-center gap-3"
            />
            <button
              type="button"
              data-registration="email"
              onClick={() => {
                tracker.action('registration_email')
                tracker.track('registration_method', { method: 'email' })
                setShowLogin(true)
              }}
              className="mt-4 text-sm text-stone-500 underline underline-offset-4 transition-colors hover:text-stone-800"
            >
              {t.registration?.cta}
            </button>
          </div>
        )}

        {/* Below the button: it describes what happens after the click, and a
            reminder placed before an action reads as one more thing to do. */}
        <p className="mt-6 text-sm text-stone-500">{t.registration?.watch_inbox}</p>

        {/* This page already shows the Google button, so the link that opens the
            modal means email, and startOnEmail skips the chooser rather than
            repeating the choice the visitor just made. In an in-app browser the
            single button means "sign me up", so the chooser is the right start.

            Reuses the shared modal: in an in-app browser it falls back to
            email and password instead of pushing the visitor out to another
            browser, which is where the questionnaire's traffic actually comes
            from. */}
        <LoginModal
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
          translations={config.login_translations}
          redirectUrl={config.after_sign_in_url}
          startOnEmail={!isInAppBrowser()}
        />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-lg px-5 py-10 sm:py-14">
      {cancelled && (
        // The cancel action reloads into this blank questionnaire, so without
        // this line the student cannot tell a cancellation from a misclick.
        <p
          className="mb-6 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-700"
          role="status"
        >
          {t.active?.cancelled}
        </p>
      )}

      <header className="mb-7">
        <h1 className="text-[1.75rem] font-semibold leading-snug tracking-tight text-stone-900">
          {t.intro_title}
        </h1>
        <p className="mt-3.5 leading-relaxed text-stone-600">{t.intro_subtitle}</p>
        <p className="mt-4 text-sm text-stone-500">{t.intro_lead}</p>

        {stepIndex === 0 && (
        <>
        {/* Before the first question, not after the last. Being contacted by a
            crowd of strangers is the objection that stops a parent filling this
            in at all, so the promises sit above the questions. */}
        <ul className="mt-5 space-y-2 rounded-xl bg-[#E6F0FF] px-4 py-3 text-sm text-stone-700">
          {['curated', 'no_contact', 'cancel'].map((key) => (
            <li key={key} className="flex items-start gap-2">
              <svg className="mt-0.5 h-4 w-4 shrink-0 text-[#3366FF]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 0 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0z" clipRule="evenodd" />
              </svg>
              {t.promises?.[key]}
            </li>
          ))}
        </ul>
        </>
        )}
      </header>

      {resumable && renderResumeModal({
        title: t.resume?.title,
        body: t.resume?.body,
        continueLabel: t.resume?.continue,
        onContinue: applyResumable,
        onRestart: discardResumable
      })}

      <div className="rounded-2xl border border-stone-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(68,60,50,0.05),0_14px_30px_-18px_rgba(68,60,50,0.22)] sm:p-7">
        <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-stone-200/70">
          <div
            className="h-full rounded-full bg-[#3366FF] transition-[width] duration-500 ease-out"
            style={{ width: `${((stepIndex + 1) / stepCount) * 100}%` }}
          />
        </div>

        <div key={step} className="wizard-step">

        {/* Says what the link filled in, so the visitor knows something was
            answered for them. ⛔ No control of its own: 上一步 is already on
            this step and now leads back to the questions that were skipped. */}
        {atSkippedBoundary && (
          <p className="mb-4 text-sm text-stone-500">{steps.prefill?.note}</p>
        )}

        {currentStep === 'subject' && (
          <fieldset>
            <legend className="mb-4 text-lg font-medium text-stone-900">{steps.subject?.question}</legend>
            <div className="space-y-2">
              {(config.subjects || []).map((option) => (
                <label
                  key={option.value}
                  className={optionClass(subject === option.value)}
                >
                  <input
                    type="radio"
                    name="subject"
                    value={option.value}
                    checked={subject === option.value}
                    onChange={() => chooseSubject(option.value)}
                    className="mr-3"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {currentStep === 'level_goals' && (
          <div className="space-y-8">
          <fieldset>
            <legend className="mb-1 text-lg font-medium text-stone-900">
              {(steps.level?.question || '').replace('%{subject}', subjectLabel)}
            </legend>
            <p className="mb-4 text-sm text-stone-500">{steps.level?.hint}</p>
            <div className="space-y-2">
              {levelOptions.map((option) => (
                <label
                  key={option.value}
                  className={optionClass(level === option.value)}
                >
                  <input
                    type="radio"
                    name="level"
                    value={option.value}
                    checked={level === option.value}
                    onChange={() => { record('select_level'); setLevel(option.value) }}
                    className="mr-3"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>

          {/* A goal the linking page stated is a fact, so it is not asked again;
              the level beside it is an inference and stays visible. */}
          {!flow.hiddenFields.includes('goals') && (
          <fieldset>
            <legend className="mb-1 text-lg font-medium text-stone-900">{steps.goals?.question}</legend>
            <p className="mb-4 text-sm text-stone-500">{steps.goals?.hint}</p>
            <div className="space-y-2">
              {(config.goals || []).map((option) => (
                <label
                  key={option.value}
                  className={optionClass(goals.includes(option.value))}
                >
                  <input
                    type="checkbox"
                    checked={goals.includes(option.value)}
                    onChange={() => toggle(goals, setGoals, option.value, 'toggle_goal')}
                    className="mr-3"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>
          )}
          </div>
        )}

        {currentStep === 'tags' && (
          <fieldset>
            <legend className="mb-1 text-lg font-medium text-stone-900">{steps.tags?.question}</legend>
            <p className="mb-4 text-sm text-stone-500">
              {(steps.tags?.hint || '').replace('%{count}', String(maxTags))}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {visibleTagOptions.map((option) => {
                const picked = tags.includes(option.value)
                // A full selection greys out what is not picked rather than
                // hiding it, so the visitor can see what they traded away.
                const atLimit = !picked && tags.length >= maxTags

                return (
                  <label
                    key={option.value}
                    className={optionClass(picked, atLimit ? 'opacity-45 !cursor-not-allowed' : '')}
                  >
                    <input
                      type="checkbox"
                      checked={picked}
                      disabled={atLimit}
                      onChange={() => toggle(tags, setTags, option.value, 'toggle_tag')}
                      className="mr-3"
                    />
                    {option.label}
                  </label>
                )
              })}
            </div>
            {hiddenTagCount > 0 && (
              <button
                type="button"
                onClick={() => { record('show_more_tags'); setTagsExpanded(true) }}
                className="mt-3 min-h-11 w-full rounded-xl border border-dashed border-stone-300 px-4 text-sm text-stone-600 transition-colors hover:border-stone-400 hover:text-stone-900"
              >
                {(steps.show_more || '').replace('%{count}', String(hiddenTagCount))}
              </button>
            )}
          </fieldset>
        )}

        {currentStep === 'schedule_note' && (
          <div className="space-y-8">
          <fieldset>
            <legend className="mb-1 text-lg font-medium text-stone-900">{steps.schedule?.question}</legend>
            <p className="mb-4 text-sm text-stone-500">{steps.schedule?.hint}</p>

            <div className="mb-2 flex gap-2">
              {Object.entries(config.day_groups || {}).map(([group, groupDays]) => (
                <button
                  key={group}
                  type="button"
                  onClick={() => { record('day_shortcut'); setDays(applyShortcut(days, groupDays)) }}
                  className={SHORTCUT_BUTTON}
                >
                  {steps.schedule?.[group]}
                </button>
              ))}

              <button
                type="button"
                onClick={() => {
                  record('anytime_shortcut')
                  const allDays = (config.days || []).map((day) => day.value)
                  const allPeriods = Object.keys(config.day_periods || {})
                  const everything = allDays.every((d) => days.includes(d)) &&
                    allPeriods.every((p) => periods.includes(p))
                  setDays(everything ? [] : allDays)
                  setPeriods(everything ? [] : allPeriods)
                }}
                className={SHORTCUT_BUTTON}
              >
                {steps.schedule?.anytime}
              </button>
            </div>

            <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {(config.days || []).map((day) => (
                <label
                  key={day.value}
                  className={optionClass(days.includes(day.value), 'justify-center !px-2 !py-2.5 text-sm')}
                >
                  <input
                    type="checkbox"
                    checked={days.includes(day.value)}
                    onChange={() => toggle(days, setDays, day.value, 'toggle_day')}
                    className="mr-2"
                  />
                  {day.label}
                </label>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2">
              {Object.keys(config.day_periods || {}).map((period) => (
                <label
                  key={period}
                  className={optionClass(periods.includes(period))}
                >
                  <input
                    type="checkbox"
                    checked={periods.includes(period)}
                    onChange={() => toggle(periods, setPeriods, period, 'toggle_period')}
                    className="mr-3"
                  />
                  {steps.schedule?.[period]}
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label htmlFor="situation" className="mb-1 block text-lg font-medium text-stone-900">
              {steps.situation?.question}
            </label>
            <p className="mb-2 text-sm text-stone-500">{steps.situation?.hint}</p>
            <textarea
              id="situation"
              value={situation}
              onChange={(event) => setSituation(event.target.value)}
              placeholder={steps.situation?.placeholder}
              maxLength={SITUATION_MAX_LENGTH}
              rows={5}
              className="w-full rounded-xl border border-stone-200 bg-white p-3.5 text-stone-900 placeholder:text-stone-500 focus-visible:border-[#3366FF] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3366FF]"
            />
          </div>
          </div>
        )}

        </div>

        {errorMessage && (
          <p className="mt-4 text-sm text-red-700" role="alert">
            {errorMessage}
          </p>
        )}

        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              record('back')
              tracker.track('wizard_back', { from_step: currentStep })
              if (atSkippedBoundary) restoreSkippedSteps()
              else goToStep(Math.max(0, step - 1))
            }}
            disabled={step === 0 && !atSkippedBoundary}
            className="min-h-11 rounded-lg px-1 text-stone-500 transition-colors hover:text-stone-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3366FF] disabled:opacity-40 disabled:hover:text-stone-500"
          >
            {t.back}
          </button>

          {step < stepCount - 1 ? (
            <button
              type="button"
              onClick={() => {
                record('next')
                if (!canAdvance()) {
                  const error = currentStep === 'subject' ? 'subject_required' : 'schedule_required'
                  tracker.track('wizard_validation_error', { step: currentStep, error })
                  setErrorMessage(errors[error])
                  return
                }
                const count = selectionCount(currentStep)
                tracker.track('wizard_step_complete', {
                  step: currentStep,
                  step_index: stepIndex,
                  skipped: count === 0,
                  selection_count: count,
                  seconds_on_step: tracker.secondsOnScreen()
                })
                tracker.completeStep()
                setErrorMessage(null)
                goToStep(step + 1)
              }}
              className={PRIMARY_BUTTON}
            >
              {t.next}
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={submitting} className={PRIMARY_BUTTON}>
              {submitting ? t.submitting : t.submit}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

StudentRequestWizard.propTypes = {
  config: PropTypes.shape({
    initial_state: PropTypes.string,
    active_request: PropTypes.object,
    submit_url: PropTypes.string.isRequired,
    after_sign_in_url: PropTypes.string,
    login_translations: PropTypes.object,
    subjects: PropTypes.array,
    levels: PropTypes.array,
    goals: PropTypes.array,
    days: PropTypes.array,
    day_groups: PropTypes.object,
    day_periods: PropTypes.object,
    translations: PropTypes.object
  }).isRequired
}
